import React, { useState, useEffect, useContext } from 'react';
import { fmtDateTime as _fmtDateTime } from '@/lib/dates';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Eye, Trash2, Filter, Star, Edit, RefreshCw } from 'lucide-react';

const FeedbackRepository = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [feedbacks, setFeedbacks] = useState([]);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [feedbackToDelete, setFeedbackToDelete] = useState(null);
  const [filter, setFilter] = useState({ rating: '', service_type: '' });

  useEffect(() => {
    fetchFeedbacks();
  }, [filter]);

  const fetchFeedbacks = async () => {
    try {
      const params = {};
      if (filter.rating) params.rating = filter.rating;
      if (filter.service_type) params.service_type = filter.service_type;
      
      const response = await axios.get(`${API}/customer-feedback`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setFeedbacks(response.data);
    } catch (error) {
      toast.error('Failed to fetch feedback');
    }
  };

  const handleView = async (feedbackId) => {
    try {
      const response = await axios.get(`${API}/customer-feedback/${feedbackId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedFeedback(response.data);
      setIsViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load feedback');
    }
  };

  const handleEdit = (feedback) => {
    navigate('/field/customer-feedback', { state: { editData: feedback } });
  };

  const handleDelete = (feedback) => {
    setFeedbackToDelete(feedback);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!feedbackToDelete) return;
    try {
      await axios.delete(`${API}/customer-feedback/${feedbackToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Feedback deleted successfully');
      setShowDeleteDialog(false);
      setFeedbackToDelete(null);
      fetchFeedbacks();
    } catch (error) {
      toast.error('Failed to delete feedback');
      setShowDeleteDialog(false);
      setFeedbackToDelete(null);
    }
  };

  const getRatingInfo = (rating) => {
    const options = {
      1: { label: 'Completely Dissatisfied', color: '#EF4444', emoji: '😞' },
      2: { label: 'Needs Improvement', color: '#F59E0B', emoji: '😕' },
      3: { label: 'Average', color: '#EAB308', emoji: '😐' },
      4: { label: 'Very Good', color: '#3B82F6', emoji: '😊' },
      5: { label: 'Excellent', color: '#6B9F3E', emoji: '🤩' }
    };
    return options[rating] || options[3];
  };

  const getRatingBadge = (rating) => {
    const info = getRatingInfo(rating);
    return (
      <Badge 
        className="text-white font-semibold"
        style={{ backgroundColor: info.color }}
      >
        {info.emoji} {rating}/5 - {info.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return _fmtDateTime(dateString);
  };

  const calculateStats = () => {
    if (feedbacks.length === 0) return { avgRating: 0, total: 0, by_rating: {} };
    
    const total = feedbacks.length;
    const sum = feedbacks.reduce((acc, fb) => acc + fb.rating, 0);
    const avgRating = (sum / total).toFixed(1);
    
    const by_rating = feedbacks.reduce((acc, fb) => {
      acc[fb.rating] = (acc[fb.rating] || 0) + 1;
      return acc;
    }, {});
    
    return { avgRating, total, by_rating };
  };

  const stats = calculateStats();

  return (
    <div className="min-h-screen bg-gray-50">
      
      
      <div className="p-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between mb-4">
            <CardTitle>Customer Feedback Repository</CardTitle>
            <Button onClick={fetchFeedbacks} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
          
          {/* Stats Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
              <p className="text-sm text-slate-600">Total Feedback</p>
              <p className="text-2xl font-bold" style={{ color: '#6B9F3E' }}>{stats.total}</p>
            </div>
            <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
              <p className="text-sm text-slate-600">Average Rating</p>
              <p className="text-2xl font-bold" style={{ color: '#6B9F3E' }}>
                <Star className="inline h-6 w-6 fill-current" /> {stats.avgRating}
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-slate-600">Excellent (5⭐)</p>
              <p className="text-2xl font-bold text-green-700">{stats.by_rating[5] || 0}</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-slate-600">Needs Attention (≤2⭐)</p>
              <p className="text-2xl font-bold text-red-700">{(stats.by_rating[1] || 0) + (stats.by_rating[2] || 0)}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Filter by Rating</Label>
              <select
                className="w-full border rounded-md px-3 py-2 mt-1"
                value={filter.rating}
                onChange={(e) => setFilter({ ...filter, rating: e.target.value })}
                data-testid="filter-rating-select"
              >
                <option value="">All Ratings</option>
                <option value="5">5 - Excellent</option>
                <option value="4">4 - Very Good</option>
                <option value="3">3 - Average</option>
                <option value="2">2 - Needs Improvement</option>
                <option value="1">1 - Completely Dissatisfied</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Filter by Service Type</Label>
              <select
                className="w-full border rounded-md px-3 py-2 mt-1"
                value={filter.service_type}
                onChange={(e) => setFilter({ ...filter, service_type: e.target.value })}
                data-testid="filter-service-type-select"
              >
                <option value="">All Services</option>
                <option value="Janitorial / Limpieza">Janitorial</option>
                <option value="Landscaping / Jardinería">Landscaping</option>
                <option value="Maintenance / Mantenimiento">Maintenance</option>
                <option value="Event Support / Apoyo de Eventos">Event Support</option>
                <option value="Special Projects / Proyectos Especiales">Special Projects</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {feedbacks.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No feedback submissions yet.</p>
            ) : (
              feedbacks.map((feedback) => (
                <div 
                  key={feedback.id} 
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                  style={{ 
                    borderLeft: `4px solid ${getRatingInfo(feedback.rating).color}` 
                  }}
                  data-testid="feedback-item"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-bold text-lg">{feedback.feedback_number}</h3>
                        {getRatingBadge(feedback.rating)}
                      </div>
                      <p className="text-sm font-medium text-slate-700">
                        {feedback.customer_name_location} - {feedback.location}
                      </p>
                      <p className="text-sm text-slate-600 mt-1">
                        Service: {feedback.service_type}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Submitted: {formatDate(feedback.submitted_at)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleView(feedback.id);
                        }}
                        data-testid="view-feedback-btn"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(feedback);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(feedback);
                        }}
                        data-testid="delete-feedback-btn"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
      </div>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Feedback Details</DialogTitle>
          </DialogHeader>
          {selectedFeedback && (
            <div className="space-y-4">
              <div 
                className="p-4 rounded-lg border-l-4"
                style={{ 
                  backgroundColor: `${getRatingInfo(selectedFeedback.rating).color}20`,
                  borderColor: getRatingInfo(selectedFeedback.rating).color
                }}
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Feedback Number</Label>
                    <p className="font-bold">{selectedFeedback.feedback_number}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Rating</Label>
                    <div className="mt-1">{getRatingBadge(selectedFeedback.rating)}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Customer/Location</Label>
                    <p className="font-medium">{selectedFeedback.customer_name_location}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Location</Label>
                    <p className="font-medium">{selectedFeedback.location}</p>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-500">Service Type</Label>
                <p className="font-medium">{selectedFeedback.service_type}</p>
              </div>

              {selectedFeedback.what_we_did_well && (
                <div>
                  <Label className="text-xs text-slate-500">What We Did Well</Label>
                  <p className="mt-1 p-3 bg-green-50 rounded border border-green-200">{selectedFeedback.what_we_did_well}</p>
                </div>
              )}

              {selectedFeedback.where_to_improve && (
                <div>
                  <Label className="text-xs text-slate-500">Where to Improve</Label>
                  <p className="mt-1 p-3 bg-orange-50 rounded border border-orange-200">{selectedFeedback.where_to_improve}</p>
                </div>
              )}

              {selectedFeedback.customer_name && (
                <div>
                  <Label className="text-xs text-slate-500">Customer Name</Label>
                  <p className="font-medium">{selectedFeedback.customer_name}</p>
                </div>
              )}

              {selectedFeedback.customer_email && (
                <div>
                  <Label className="text-xs text-slate-500">Customer Email</Label>
                  <p className="font-medium">{selectedFeedback.customer_email}</p>
                </div>
              )}

              {selectedFeedback.signature && (
                <div>
                  <Label className="text-xs text-slate-500">Signature</Label>
                  <p className="font-medium italic">{selectedFeedback.signature}</p>
                </div>
              )}

              <div>
                <Label className="text-xs text-slate-500">Submitted At</Label>
                <p className="text-sm">{formatDate(selectedFeedback.submitted_at)}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete feedback "{feedbackToDelete?.feedback_number}"? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete Feedback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeedbackRepository;
